import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  WhatsappLogo, ShieldWarning, CircleNotch, Check, X,
  PaperPlaneTilt, Phone, Clock, Lightning,
} from "@phosphor-icons/react";

/**
 * Configuração do canal "Alertas Críticos Master" via WhatsApp.
 * Envio só dispara para eventos da whitelist e respeita cooldown.
 */
export default function AdminAlertSettings() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ enabled: false, phone: "", cooldown_minutes: 30 });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/alert-settings");
    setData(data);
    setForm({
      enabled: !!data.enabled,
      phone: data.phone || "",
      cooldown_minutes: data.cooldown_minutes || 30,
    });
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: form.enabled,
        phone: form.phone,
        cooldown_minutes: parseInt(form.cooldown_minutes, 10) || 30,
      };
      await api.put("/admin/alert-settings", payload);
      toast.success("Configuração salva");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!form.phone) {
      toast.error("Informe o telefone primeiro");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/alert-settings/test");
      if (data.sent) {
        toast.success("Mensagem de teste enviada — verifique o WhatsApp");
      } else {
        toast.error(`Falha: ${data.reason || data.error || "erro desconhecido"}`);
      }
    } catch (e) {
      toast.error("Falha ao enviar teste");
    } finally {
      setTesting(false);
    }
  };

  if (!data) {
    return <div className="px-4 md:px-8 py-14 md:py-16 text-center"><CircleNotch size={20} className="animate-spin text-slate-500 inline" /></div>;
  }

  const whitelist = data.whitelist || [];

  return (
    <div className="px-4 md:px-8 py-6 md:py-7 max-w-[920px] mx-auto">
      <header className="mb-7">
        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">Plataforma</div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight text-white mt-1 flex items-center gap-2.5">
          <WhatsappLogo size={26} weight="regular" className="text-emerald-400" />
          Alertas Críticos Master
        </h1>
        <p className="text-sm text-slate-400 mt-1.5">
          Receba notificações via WhatsApp <strong className="text-slate-200">apenas</strong> para eventos realmente críticos. Eventos médios continuam disponíveis no painel.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-5">
        {/* Main panel */}
        <div className="space-y-5">
          <section className="bg-white/[0.03] border border-white/10 rounded-lg p-6" data-testid="alert-settings-form">
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium">Canal</div>
                <h2 className="font-heading text-lg font-semibold text-white mt-1">WhatsApp via Z-API</h2>
              </div>
              <button
                onClick={() => setForm({ ...form, enabled: !form.enabled })}
                data-testid="alert-settings-enabled-toggle"
                className={`relative w-12 h-6 rounded-full transition-colors ${form.enabled ? "bg-emerald-500" : "bg-slate-700"}`}
              >
                <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.enabled ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
            </div>

            {/* Phone */}
            <label className="block">
              <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-2 flex items-center gap-1.5">
                <Phone size={12} weight="regular" /> Telefone do master (com DDD)
              </span>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="11999998888 ou 5511999998888"
                data-testid="alert-settings-phone"
                className="w-full bg-white/5 border border-white/10 rounded-md px-3.5 py-2.5 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-white/30 tabular-nums"
              />
              <span className="text-[10px] text-slate-600 mt-1.5 block">
                Será normalizado para DDI 55 automaticamente. {data.phone_masked && <>Atual: <strong className="text-slate-300">{data.phone_masked}</strong></>}
              </span>
            </label>

            {/* Cooldown */}
            <label className="block mt-5">
              <span className="block text-[11px] uppercase tracking-[0.16em] text-slate-400 font-medium mb-2 flex items-center gap-1.5">
                <Clock size={12} weight="regular" /> Cooldown por evento
              </span>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="5"
                  max="1440"
                  value={form.cooldown_minutes}
                  onChange={(e) => setForm({ ...form, cooldown_minutes: e.target.value })}
                  data-testid="alert-settings-cooldown"
                  className="w-24 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-white/30 tabular-nums"
                />
                <span className="text-[12px] text-slate-400">minutos sem repetir o mesmo evento para o mesmo tenant</span>
              </div>
            </label>

            <div className="mt-6 pt-5 border-t border-white/5 flex flex-wrap items-center gap-2">
              <button
                onClick={save}
                disabled={saving}
                data-testid="alert-settings-save"
                className="inline-flex items-center gap-2 bg-white text-slate-900 hover:bg-slate-200 px-4 py-2 rounded-md text-[13px] font-semibold transition-colors disabled:opacity-60"
              >
                {saving ? <CircleNotch size={12} className="animate-spin" /> : <Check size={12} weight="bold" />}
                {saving ? "Salvando…" : "Salvar configuração"}
              </button>
              <button
                onClick={sendTest}
                disabled={testing || !form.phone}
                data-testid="alert-settings-test"
                className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-400/30 text-emerald-300 hover:bg-emerald-500/20 px-4 py-2 rounded-md text-[13px] font-semibold transition-colors disabled:opacity-60"
              >
                {testing ? <CircleNotch size={12} className="animate-spin" /> : <PaperPlaneTilt size={12} weight="regular" />}
                {testing ? "Enviando…" : "Enviar mensagem de teste"}
              </button>
              {data.last_sent_at && (
                <span className="text-[11px] text-slate-500 ml-auto">
                  Último envio: {new Date(data.last_sent_at).toLocaleString("pt-BR")}
                </span>
              )}
            </div>
          </section>

          {/* Preview */}
          <section className="bg-white/[0.03] border border-white/10 rounded-lg p-6">
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-medium mb-3">Preview da mensagem</div>
            <div className="bg-emerald-500/[0.06] border border-emerald-400/20 rounded-md p-4 font-mono text-[12px] text-slate-200 leading-relaxed whitespace-pre-wrap">
{`🚨 *Tenant crítico: BYD Asa Norte*
_Plano PRO · 8a3f0c12_

*Motivos:*
• WhatsApp desconectou
• Score caiu de *healthy* → *critical*
• Margem ficou negativa

*Ações:*
🔗 Abrir tenant: https://app.agentus.com.br/admin-master/tenants/...
✉️ Suporte: suporte@agentus.com.br

_Responda IGNORE para silenciar este tenant por 24h._`}
            </div>
          </section>
        </div>

        {/* Sidebar: whitelist */}
        <aside className="bg-white/[0.03] border border-white/10 rounded-lg p-5 self-start">
          <div className="flex items-center gap-2 mb-3">
            <ShieldWarning size={14} weight="regular" className="text-amber-400" />
            <h3 className="font-heading text-sm font-semibold text-white">Eventos disparadores</h3>
          </div>
          <p className="text-[11px] text-slate-400 leading-relaxed mb-4">
            Apenas estes eventos enviam WhatsApp. Tudo mais fica no sino do painel.
          </p>
          <ul className="space-y-2">
            {whitelist.map((c) => (
              <li key={c} className="flex items-start gap-2">
                <Lightning size={11} weight="fill" className="text-amber-400 shrink-0 mt-1" />
                <code className="text-[11px] text-slate-300 font-mono break-all">{c}</code>
              </li>
            ))}
            <li className="flex items-start gap-2 pt-2 border-t border-white/5">
              <Lightning size={11} weight="fill" className="text-rose-400 shrink-0 mt-1" />
              <span className="text-[11px] text-slate-300">level_drop:* → critical</span>
            </li>
          </ul>
          <div className="mt-5 pt-4 border-t border-white/5 text-[10px] text-slate-500 leading-relaxed">
            <strong className="text-slate-400">Anti-flood:</strong> mesmo evento para o mesmo tenant não repete dentro do cooldown configurado.
          </div>
        </aside>
      </div>
    </div>
  );
}
